import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const TEAL_GLOW = "rgba(79,205,196,0.06)";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const TABLE_BORDER = "rgba(255,255,255,0.08)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: "clamp(18px, 2.5vw, 22px)", color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.01em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: "0.9em", color: TEXT, background: "rgba(255,255,255,0.05)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function PullQuote({ children }) {
  return (
    <div style={{ borderLeft: `3px solid ${TEAL}`, paddingLeft: 20, margin: "32px 0" }}>
      <p style={{ fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.5, margin: 0 }}>{children}</p>
    </div>
  );
}
function InlineCTA({ children, href, label }) {
  return (
    <div style={{ margin: "40px 0", padding: "24px 28px", borderRadius: 14, background: TEAL_DIM, border: "1px solid rgba(78,205,196,0.25)" }}>
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>{children}</p>
      <a href={href} style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}>
        {label} &#8594;
      </a>
    </div>
  );
}
function TLDRCard({ children }) {
  return (
    <div style={{ margin: "28px 0 36px", padding: "24px 28px", borderRadius: 14, background: TEAL_GLOW, border: `1px solid ${TABLE_BORDER}` }}>
      <div style={{ fontFamily: "var(--display)", fontSize: 12, fontWeight: 700, color: TEAL, marginBottom: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}>TL;DR</div>
      {children}
    </div>
  );
}

const faqs = [
  {
    q: "What is OAI-AdsBot?",
    a: "OAI-AdsBot is OpenAI's web crawler that validates ChatGPT ad landing pages. When you submit a landing page through OpenAI's Ads Manager, OAI-AdsBot visits it to confirm policy compliance and gather content for ad relevance scoring. It was added to OpenAI's public bot documentation on April 23, 2026.",
  },
  {
    q: "How is OAI-AdsBot different from GPTBot, OAI-SearchBot, and ChatGPT-User?",
    a: "GPTBot collects training data for foundation models. OAI-SearchBot indexes pages for ChatGPT Search retrieval. ChatGPT-User fires when a user asks ChatGPT to fetch a specific URL. OAI-AdsBot only crawls landing pages that advertisers have explicitly submitted, and the data it collects is excluded from training.",
  },
  {
    q: "Does OAI-AdsBot use my content to train OpenAI's models?",
    a: "No. OpenAI states explicitly that the data collected by OAI-AdsBot is not used to train generative AI foundation models. If you are concerned about training-data inclusion, OAI-AdsBot is the safest of OpenAI's four crawlers to allow. The training risk is GPTBot.",
  },
  {
    q: "Will Cloudflare or my WAF block OAI-AdsBot by default?",
    a: "It can. Cloudflare, Akamai, Fastly, Imperva, and DataDome can challenge or block unfamiliar bots, and a blocked OAI-AdsBot can get your ad rejected. Allowlist OAI-AdsBot explicitly in your WAF rules.",
  },
  {
    q: "Has OpenAI published an IP range file for OAI-AdsBot?",
    a: "Yes. OpenAI now publishes OAI-AdsBot's IP addresses at openai.com/adsbot.json, alongside gptbot.json, searchbot.json and chatgpt-user.json for its other crawlers. The file did not exist when this post first ran in April 2026. OpenAI recommends pointing any fixed allowlist at the published file and combining it with user-agent checks and your provider's bot verification, rather than relying on addresses seen in your logs.",
  },
  {
    q: "Does OAI-AdsBot respect robots.txt?",
    a: "Yes. OpenAI's guidance for advertisers says its crawlers respect robots.txt rules and that if access is disallowed, crawling stops immediately. Its own example uses User-agent: OAI-AdsBot with Allow: /. OpenAI says you must allow OAI-AdsBot for ChatGPT Ads, and recommends allowing OAI-SearchBot as well.",
  },
  {
    q: "What happens if my landing page blocks OAI-AdsBot?",
    a: "OAI-AdsBot cannot validate a page it cannot reach, so the ad will likely be rejected. In Ads Manager the ad shows Not serving, and hovering over the status shows the reason; OpenAI lists landing pages that block its user agents or IP addresses among the common causes of a rejected ad. Check both server and CDN logs after every campaign launch, especially on pages behind aggressive WAF rules.",
  },
  {
    q: "How do I allowlist OAI-AdsBot?",
    a: "Add the user-agent to robots.txt, allowlist it in Cloudflare, Akamai, Fastly, or other CDN, render landing-page content server-side, verify visits against OpenAI's published IP ranges, and check your robots.txt with the free AI Bot Access Checker. The 6-step playbook in this post walks through each.",
  },
  {
    q: "Does OAI-AdsBot render JavaScript?",
    a: "OpenAI has not documented OAI-AdsBot's rendering behavior, so treat it as a crawler that may not execute client-side JavaScript and serve your landing-page content as server-rendered HTML. If the headline, offer, and policy-relevant copy only appear after a client-side framework hydrates, OAI-AdsBot can validate against an empty or partial page and stall the ad in review. This is why the allowlist playbook lists server-side rendering alongside the robots.txt and WAF steps: reachability gets the bot to the page, but server-rendered HTML is what guarantees it can read the page once it arrives.",
  },
];

const howtoSteps = [
  {
    name: "Add OAI-AdsBot to robots.txt",
    text: "Add a User-agent: OAI-AdsBot block to your robots.txt with Allow: / so the crawler can fetch your landing page. OpenAI's own advertiser guidance uses exactly this block, and recommends allowing OAI-SearchBot as well.",
  },
  {
    name: "Allowlist the user-agent in Cloudflare WAF",
    text: "Cloudflare's bot protection may challenge or block OAI-AdsBot. If you use Super Bot Fight Mode, create a WAF custom rule that lets requests whose User-Agent header contains OAI-AdsBot/1.0 skip it. Bot Fight Mode cannot be skipped by custom rules, so if it is on and challenging OAI-AdsBot, switch it off. Without this, the ad can be rejected.",
  },
  {
    name: "Allowlist in Akamai, Fastly, or other CDN edge",
    text: "If you run an edge layer beyond Cloudflare, repeat the user-agent allowlist there. Akamai Bot Manager and Fastly's signal sciences module both treat unknown bots as suspicious by default and may block OAI-AdsBot before it reaches your origin server.",
  },
  {
    name: "Render landing-page content server-side",
    text: "OAI-AdsBot's JavaScript execution capability is undocumented. To remove the risk, render all above-the-fold landing-page content server-side or use SSR. JavaScript-only single-page apps can fail validation when the bot cannot execute client-side rendering.",
  },
  {
    name: "Verify visits against OpenAI's published IP ranges",
    text: "OpenAI publishes OAI-AdsBot's IP addresses at openai.com/adsbot.json. Check that requests claiming to be OAI-AdsBot come from an address inside those ranges, and point any IP allowlist at the published file rather than at addresses copied from your logs.",
  },
  {
    name: "Check robots.txt with the AI Bot Access Checker",
    text: "Run your landing page URL through the free AI Bot Access Checker to confirm your robots.txt allows OAI-AdsBot and to see whether the site is served through Cloudflare. Re-test after every WAF, CDN, or DNS change, and especially before launching a new ChatGPT Ads campaign. Bot allowlists drift when teams update infrastructure.",
  },
];

// ── Visual 1: 4-bot OpenAI taxonomy table ───────────────────────────
function TaxonomyTable() {
  const cellBase = {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_DIM,
    lineHeight: 1.55,
    verticalAlign: "top",
    borderTop: `1px solid ${TABLE_BORDER}`,
    borderRight: `1px solid ${TABLE_BORDER}`,
  };
  const headerCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 700,
    background: "rgba(255,255,255,0.04)",
    fontSize: 13,
    letterSpacing: "0.02em",
  };
  const labelCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 600,
    background: "rgba(255,255,255,0.02)",
    whiteSpace: "nowrap",
    fontSize: 13,
  };
  const gapCell = {
    ...cellBase,
    background: TEAL_GLOW,
    color: TEXT_DIM,
    fontStyle: "italic",
    borderRight: `1px solid ${TABLE_BORDER}`,
  };
  const monoCell = {
    ...cellBase,
    fontFamily: "var(--mono)",
    fontSize: 11,
    lineHeight: 1.4,
    wordBreak: "break-all",
  };

  return (
    <div className="blog-scroll-wrap" style={{ margin: "28px 0 36px", overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12 }}>
      <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", borderSpacing: 0, fontFamily: "var(--body)" }} aria-label="The 4 OpenAI crawlers compared across purpose, user-agent, version, robots.txt token, IP range file, training use, and recommended stance.">
        <thead>
          <tr>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none" }}></th>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none" }}>GPTBot</th>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none" }}>OAI-SearchBot</th>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none" }}>ChatGPT-User</th>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none", borderRight: "none", background: TEAL_GLOW, color: TEAL }}>OAI-AdsBot</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={labelCell}>Purpose</td>
            <td style={cellBase}>Foundation model training data collection</td>
            <td style={cellBase}>Index pages for ChatGPT Search retrieval</td>
            <td style={cellBase}>User-initiated browsing inside ChatGPT</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW }}>Validate safety and relevance of ChatGPT ad landing pages</td>
          </tr>
          <tr>
            <td style={labelCell}>User-agent</td>
            <td style={monoCell}>Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; GPTBot/1.3; +https://openai.com/gptbot</td>
            <td style={monoCell}>Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36; compatible; OAI-SearchBot/1.3; +https://openai.com/searchbot</td>
            <td style={monoCell}>Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; ChatGPT-User/1.0; +https://openai.com/bot</td>
            <td style={{ ...monoCell, borderRight: "none", background: TEAL_GLOW }}>Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; OAI-AdsBot/1.0; +https://openai.com/adsbot</td>
          </tr>
          <tr>
            <td style={labelCell}>Version</td>
            <td style={cellBase}>1.3</td>
            <td style={cellBase}>1.3</td>
            <td style={cellBase}>1.0</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW }}>1.0</td>
          </tr>
          <tr>
            <td style={labelCell}>robots.txt token</td>
            <td style={{ ...cellBase, fontFamily: "var(--mono)", fontSize: 12 }}>GPTBot</td>
            <td style={{ ...cellBase, fontFamily: "var(--mono)", fontSize: 12 }}>OAI-SearchBot</td>
            <td style={{ ...cellBase, fontFamily: "var(--mono)", fontSize: 12 }}>ChatGPT-User <span style={{ color: TEXT_DIM, fontFamily: "var(--body)", fontStyle: "italic" }}>(rules may not apply)</span></td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW, fontFamily: "var(--mono)", fontSize: 12 }}>OAI-AdsBot <span style={{ color: TEXT_DIM, fontFamily: "var(--body)", fontStyle: "italic" }}>(in OpenAI's advertiser guidance)</span></td>
          </tr>
          <tr>
            <td style={labelCell}>IP range file</td>
            <td style={cellBase}><A href="https://openai.com/gptbot.json">gptbot.json</A></td>
            <td style={cellBase}><A href="https://openai.com/searchbot.json">searchbot.json</A></td>
            <td style={cellBase}><A href="https://openai.com/chatgpt-user.json">chatgpt-user.json</A></td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW }}><A href="https://openai.com/adsbot.json">adsbot.json</A></td>
          </tr>
          <tr>
            <td style={labelCell}>Used for training?</td>
            <td style={cellBase}>Yes</td>
            <td style={cellBase}>No</td>
            <td style={cellBase}>No</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW, color: TEAL, fontWeight: 600 }}>No</td>
          </tr>
          <tr>
            <td style={labelCell}>Recommended stance</td>
            <td style={cellBase}>Block (training); Allow if you opt in</td>
            <td style={cellBase}>Allow (search citations)</td>
            <td style={cellBase}>Allow (user explicitly asked)</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW, color: TEAL, fontWeight: 600 }}>Allow (or your ChatGPT ads break)</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Visual 4: SVG mini-timeline of February to May 2026 ChatGPT Ads events ─────
function TimelineSVG() {
  return (
    <div style={{ margin: "32px 0 36px", padding: "20px 12px 8px", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12, background: "rgba(255,255,255,0.02)" }}>
      <svg viewBox="0 0 900 170" style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-labelledby="oai-adsbot-timeline-title oai-adsbot-timeline-desc">
        <title id="oai-adsbot-timeline-title">ChatGPT Ads timeline, February to May 2026</title>
        <desc id="oai-adsbot-timeline-desc">Six events on a horizontal timeline: Feb 9 testing started, Apr 10 self-serve launched, Apr 13 minimum dropped to fifty thousand dollars, Apr 21-22 dashboard reveal, Apr 23 OAI-AdsBot documented (highlighted as the news anchor for this post), May 5 opened to every US advertiser with no account minimum.</desc>

        <line x1="40" y1="92" x2="860" y2="92" stroke="rgba(255,255,255,0.18)" strokeWidth="2" />

        <circle cx="70" cy="92" r="6" fill="#a0a0a0" />
        <text x="70" y="64" textAnchor="middle" fill="#EDEDED" fontSize="13" fontWeight="600" fontFamily="var(--display)">Feb 9</text>
        <text x="70" y="120" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">Ads testing</text>
        <text x="70" y="135" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">started</text>

        <circle cx="290" cy="92" r="6" fill="#a0a0a0" />
        <text x="290" y="64" textAnchor="middle" fill="#EDEDED" fontSize="13" fontWeight="600" fontFamily="var(--display)">Apr 10</text>
        <text x="290" y="120" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">Self-serve</text>
        <text x="290" y="135" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">launched</text>

        <circle cx="450" cy="92" r="6" fill="#a0a0a0" />
        <text x="450" y="64" textAnchor="middle" fill="#EDEDED" fontSize="13" fontWeight="600" fontFamily="var(--display)">Apr 13</text>
        <text x="450" y="120" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">$50K minimum</text>
        <text x="450" y="135" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">(was $200K)</text>

        <circle cx="600" cy="92" r="6" fill="#a0a0a0" />
        <text x="600" y="64" textAnchor="middle" fill="#EDEDED" fontSize="13" fontWeight="600" fontFamily="var(--display)">Apr 21-22</text>
        <text x="600" y="120" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">Dashboard</text>
        <text x="600" y="135" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">reveal</text>

        <circle cx="730" cy="92" r="14" fill="none" stroke="#4FCDC4" strokeWidth="1.5" opacity="0.4" />
        <circle cx="730" cy="92" r="9" fill="#4FCDC4" />
        <text x="730" y="50" textAnchor="middle" fill="#4FCDC4" fontSize="13" fontWeight="700" fontFamily="var(--display)">Apr 23</text>
        <text x="730" y="120" textAnchor="middle" fill="#EDEDED" fontSize="11" fontWeight="600" fontFamily="var(--body)">OAI-AdsBot</text>
        <text x="730" y="135" textAnchor="middle" fill="#EDEDED" fontSize="11" fontWeight="600" fontFamily="var(--body)">documented</text>
        <text x="730" y="150" textAnchor="middle" fill="#a0a0a0" fontSize="10" fontStyle="italic" fontFamily="var(--body)">(this post)</text>

        <circle cx="860" cy="92" r="6" fill="#a0a0a0" />
        <text x="860" y="64" textAnchor="middle" fill="#EDEDED" fontSize="13" fontWeight="600" fontFamily="var(--display)">May 5</text>
        <text x="860" y="120" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">Opens to every</text>
        <text x="860" y="135" textAnchor="middle" fill="#a0a0a0" fontSize="11" fontFamily="var(--body)">US advertiser</text>
      </svg>
    </div>
  );
}

// ── Visual 3: Diagnostic table - "Why your ChatGPT ad isn't approved" ─
function DiagnosticTable() {
  const rows = [
    {
      symptom: "Ad submitted but never goes live",
      cause: "OAI-AdsBot blocked at WAF (Cloudflare, Akamai, Fastly)",
      fix: "Allowlist the user-agent at every CDN edge (Steps 2 and 3)",
    },
    {
      symptom: "Inconsistent approval across campaigns",
      cause: "JavaScript-only landing page; OAI-AdsBot may not execute JS",
      fix: "Render critical above-the-fold content server-side (Step 4)",
    },
    {
      symptom: "Ad approved but low impression volume",
      cause: "Page intent unclear; bot can't match relevance",
      fix: "Tighten H1, make offer explicit above the fold, add schema",
    },
    {
      symptom: "Server logs show no OAI-AdsBot visits",
      cause: "Bot blocked at CDN edge before reaching origin",
      fix: "Check CDN logs, not just origin (Step 5 confirms a visit is really OpenAI)",
    },
    {
      symptom: "Ad approved, then immediate bounce",
      cause: "Landing page mismatches ad creative",
      fix: "Match landing-page H1 to ad copy verbatim",
    },
    {
      symptom: "Approved on staging, blocked on production",
      cause: "WAF rules differ across environments",
      fix: "Sync WAF allowlist between staging and prod",
    },
    {
      symptom: "Validation pending forever",
      cause: "Server errors, or rate limiting (429 responses) when the crawler visits",
      fix: "Test with curl -A \"OAI-AdsBot/1.0\" https://your-page",
    },
  ];

  const cellBase = {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_DIM,
    lineHeight: 1.55,
    verticalAlign: "top",
    borderTop: `1px solid ${TABLE_BORDER}`,
    borderRight: `1px solid ${TABLE_BORDER}`,
  };
  const headerCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 700,
    background: "rgba(255,255,255,0.04)",
    borderTop: "none",
    textAlign: "left",
  };

  return (
    <div className="blog-scroll-wrap" style={{ margin: "24px 0 36px", overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12 }}>
      <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", borderSpacing: 0, fontFamily: "var(--body)" }} aria-label="Symptom, cause, and fix for common OAI-AdsBot validation failures.">
        <thead>
          <tr>
            <th style={{ ...headerCell, width: "30%" }}>Symptom</th>
            <th style={{ ...headerCell, width: "35%" }}>Likely cause</th>
            <th style={{ ...headerCell, width: "35%", borderRight: "none" }}>Fix</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...cellBase, color: TEXT, fontWeight: 600 }}>{r.symptom}</td>
              <td style={cellBase}>{r.cause}</td>
              <td style={{ ...cellBase, borderRight: "none" }}>{r.fix}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function Content() {
  return (
    <article>
      {/* ── Intro ── */}
      <P>
        OAI-AdsBot is OpenAI's web crawler that validates ChatGPT ad landing pages, checking policy compliance and gathering content for ad relevance scoring when a page is submitted through Ads Manager. On April 23, 2026, OpenAI quietly added this fourth bot to its public crawler documentation. When you submit an ad, <Strong>OAI-AdsBot</Strong> may visit the landing page to check it. An advertiser may not learn the bot exists until an ad is rejected because an edge provider such as Cloudflare or Akamai blocked it before it reached the page.
      </P>
      <P>
        This post walks through what OAI-AdsBot is and what it does, how it sits alongside the three other OpenAI crawlers (<Strong>GPTBot</Strong>, <Strong>OAI-SearchBot</Strong>, <Strong>ChatGPT-User</Strong>), how to verify that a visit really comes from OpenAI now that it publishes OAI-AdsBot's IP addresses, and a 6-step playbook to allowlist OAI-AdsBot correctly.
      </P>

      <TLDRCard>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li>When you submit a ChatGPT ad, OpenAI may send OAI-AdsBot to its landing page to check it complies with OpenAI's ad policies and to gather relevance signals.</Li>
          <Li>The training-exclusion clause: data collected by OAI-AdsBot is explicitly excluded from foundation-model training. Of OpenAI's four crawlers, this is the safest one to allow.</Li>
          <Li>Since this post first ran in April, OpenAI has published OAI-AdsBot's IP addresses (openai.com/adsbot.json) and documented how its crawlers treat robots.txt. The remaining risk is your own bot protection: Cloudflare or Akamai can block the bot and get your ad rejected.</Li>
        </ul>
      </TLDRCard>

      {/* ── Section 2: What OAI-AdsBot does ── */}
      <H2 id="what-oai-adsbot-does">What OAI-AdsBot does and the one thing it does not do</H2>
      <P>
        <A href="https://developers.openai.com/api/docs/bots">OpenAI's bot documentation</A> states OAI-AdsBot's purpose plainly: <em>"OAI-AdsBot is used to validate the safety of web pages submitted as ads on ChatGPT. When you submit an ad, OpenAI may visit the landing page to ensure it complies with our policies."</em> Validation runs on-demand. The bot only visits a landing page after the ad is submitted, not continuously.
      </P>
      <P>
        A second function: relevance scoring. OpenAI continues, <em>"We may also use content from the landing page to determine when it's most relevant to show the ad to users."</em> The implication: your landing page's content (above-fold copy, headline match, <A href="/glossary/schema-markup">schema markup</A>) is an input to ad targeting, not just to ad approval. Treat the landing page as part of the ad creative, not as a separate destination.
      </P>
      <P>
        Then the most advertiser-friendly clause in the entire bot documentation: <em>"the data collected by OAI-AdsBot is not used to train generative AI foundation models."</em> Of OpenAI's four documented crawlers, only OAI-AdsBot has this explicit training-exclusion guarantee. <Strong>If you are worried about your content being absorbed into a future GPT model, OAI-AdsBot is the one OpenAI bot you can confidently allow.</Strong> The training risk is GPTBot, not OAI-AdsBot.
      </P>

      <PullQuote>
        The data collected by OAI-AdsBot is not used to train generative AI foundation models.
      </PullQuote>

      {/* ── Section 3: 4-bot taxonomy + Visual 1 ── */}
      <H2 id="four-bot-taxonomy">The 4-bot OpenAI crawler taxonomy</H2>
      <P>
        Most "allow OpenAI bots" advice on the public web treats them as a single decision. They are not. OpenAI operates four documented crawlers, each with a different purpose, a different version, and a different recommended stance. Confuse them and you either block valuable retrieval traffic or invite training-data scraping you wanted to opt out of.
      </P>

      <TaxonomyTable />

      <H3>GPTBot - block by default</H3>
      <P>
        GPTBot collects training data for OpenAI's foundation models. Allow it only if you actively want your content included in future GPT training runs. Most B2B sites should block GPTBot in robots.txt and rely on OAI-SearchBot for ChatGPT visibility instead.
      </P>
      <H3>OAI-SearchBot - allow</H3>
      <P>
        OAI-SearchBot indexes pages for ChatGPT Search retrieval. Block this and you exit ChatGPT Search citations entirely. Note the user-agent format: this is the only OpenAI bot using the full Chrome 131 macOS string, which suggests it renders pages with full browser behavior more closely than the other three. For the deeper play on which OpenAI crawler to block and which to allow (and the edge-layer blocking that can silently break both), see <A href="/blog/gptbot-vs-oai-searchbot">the binary GPTBot vs OAI-SearchBot decision</A>.
      </P>
      <H3>ChatGPT-User - allow</H3>
      <P>
        ChatGPT-User fires when a ChatGPT user explicitly asks the model to fetch a specific URL. The visit is user-initiated, so OpenAI notes that "robots.txt rules may not apply" for this bot. The user asked for the content directly. Blocking it breaks user-initiated browsing inside ChatGPT.
      </P>
      <H3>OAI-AdsBot - allow (or your ChatGPT ads break)</H3>
      <P>
        OAI-AdsBot is the bot this post covers. Block it and your <A href="/glossary/chatgpt-ads">ChatGPT ad campaigns</A> will fail to validate. The data is not used for training, the visits are scoped to landing pages you have explicitly submitted, and the operational cost of blocking is high (rejected ads). Allow it.
      </P>

      {/* ── Section 4: Verification problem ── */}
      <H2 id="verification-problem">How to verify OAI-AdsBot now that OpenAI publishes its IP addresses</H2>
      <P>
        When this post first ran in April 2026, OpenAI published IP range files for three of its crawlers: <A href="https://openai.com/gptbot.json">gptbot.json</A>, <A href="https://openai.com/searchbot.json">searchbot.json</A> and <A href="https://openai.com/chatgpt-user.json">chatgpt-user.json</A>. OAI-AdsBot had none, which left security teams no way to verify it by IP. That gap is closed. OpenAI's bot documentation now lists <A href="https://openai.com/adsbot.json">openai.com/adsbot.json</A> as OAI-AdsBot's published IP addresses, and the file's own timestamp reads May 12, 2026.
      </P>
      <P>
        It matters because of how Cloudflare, Akamai, Fastly, Imperva, and DataDome decide whether a request is a real bot or a spoofer. Their primary signal is IP. <Strong>User-agent inspection is a hint; IP inspection is a verification.</Strong>
      </P>
      <P>
        With the file published, your WAF can now check that a request claiming to be OAI-AdsBot really comes from OpenAI by matching its source IP against adsbot.json, the same way it already could for GPTBot and OAI-SearchBot. Allowlisting by user-agent alone remains a trust assumption, because any client can put any string in the User-Agent header.
      </P>
      <P>
        Two kinds of teams gain the most:
      </P>
      <ol style={{ paddingLeft: 20, margin: "0 0 18px", color: TEXT_DIM }}>
        <Li><Strong>Conservative-on-bots advertisers</Strong> who want to allow legitimate OpenAI traffic but block unverified scrapers. They no longer have to choose between letting some spoofed traffic through and blocking the real bot along with the spoofers.</Li>
        <Li><Strong>WAF-managed sites at scale</Strong> (large enterprises, agency-managed accounts) where security teams add allowlists by IP range as policy. Before adsbot.json existed they had nothing to add to the allowlist; now the rule can reference the published file.</Li>
      </ol>
      <P>
        OpenAI adds one caution in its <A href="https://help.openai.com/en/articles/20001243-advertiser-guidance-for-allowing-openai-web-crawlers">crawler guidance for advertisers</A>: because crawler infrastructure may change, do not rely only on IP addresses you happened to see in your logs. It recommends combining user-agent identification, verified bot programs where your provider supports them, firewall allowlists, robots.txt behavior and provider-level bot verification, and pointing any fixed IP allowlist at the published file. Step 5 of the playbook below shows the check.
      </P>

      <PullQuote>
        OpenAI now publishes OAI-AdsBot's IP addresses. Verify by IP where your firewall supports it, and treat the user-agent alone as a hint, not proof.
      </PullQuote>

      {/* ── Section 5: Robots.txt token honesty ── */}
      <H2 id="robots-txt-honesty">Robots.txt: what OpenAI now documents for OAI-AdsBot</H2>
      <P>
        When this post first ran, OpenAI's bot documentation named robots.txt tokens for GPTBot and OAI-SearchBot, noted that robots.txt rules may not apply to user-initiated ChatGPT-User visits, and said nothing about OAI-AdsBot. Its developer page still names only GPTBot and OAI-SearchBot as robots.txt tags, but OpenAI's Help Center guidance for advertisers now covers OAI-AdsBot directly.
      </P>
      <P>
        That guidance says, &ldquo;You must allow OAI-AdsBot,&rdquo; recommends allowing OAI-SearchBot too, and states that OpenAI crawlers respect robots.txt rules: if access is disallowed, &ldquo;crawling will stop immediately.&rdquo; Its own example uses <Code>User-agent: OAI-AdsBot</Code> with <Code>Allow: /</Code>.
      </P>
      <P>
        So use <Code>User-agent: OAI-AdsBot</Code>, as OpenAI does. Capitalization does not matter: the robots exclusion standard (<A href="https://www.rfc-editor.org/rfc/rfc9309">RFC 9309</A>) requires crawlers to match user-agent names case-insensitively. Then confirm in your server and CDN logs that the bot actually reaches the page after you submit an ad.
      </P>
      <P>
        If you also run a firewall or bot-protection service, robots.txt is only the first layer. OpenAI's guidance names two more layers that can block its crawlers: bot mitigation that returns 403 errors, and human-verification checks such as CAPTCHAs and JavaScript challenges. Steps 2 and 3 below cover the firewall layer; exempt OpenAI's crawlers from any CAPTCHA or JavaScript challenge too.
      </P>

      {/* ── Section 6: Advertiser implications + SVG timeline ── */}
      <H2 id="advertiser-implications">What this means for ChatGPT Ads advertisers</H2>
      <P>
        ChatGPT Ads has accelerated faster than any new ad platform in recent memory. Each step compresses the timeline.
      </P>

      <TimelineSVG />

      <P>
        The OAI-AdsBot documentation update (April 23) sits in the middle of an aggressive 25-day burst: self-serve launch April 10, $50K minimum cut April 13 (down from $200K), dashboard reveal April 21-22, bot documented April 23, all-US-advertisers + minimum removed entirely on May 5. <A href="/blog/how-to-set-up-chatgpt-ad-campaign">The dashboard reveal post</A> covers what the wizard surfaces; this post covers what happens after you click Submit.
      </P>
      <P>
        In practical terms: you build a campaign in the Ads Manager, you submit a landing page URL, and OpenAI may send OAI-AdsBot to check it. If your WAF (Cloudflare, Akamai, Fastly, Imperva, DataDome) blocks the bot, the check fails. In Ads Manager the ad shows Not serving, and hovering over its status shows the reason. OpenAI's <A href="https://help.openai.com/en/articles/20001217-troubleshooting-common-issues">troubleshooting guide</A> lists landing pages that block OpenAI's user agents or IP addresses among the common causes of a rejected ad. Our <A href="/blog/chatgpt-ads-no-impressions">zero-impressions troubleshooting guide</A> covers the crawler alongside every other level that can block delivery.
      </P>
      <P>
        It is a common, avoidable reason new ChatGPT ads fail review. It is not your creative. It is not your bid strategy. It is your infrastructure refusing to let OpenAI verify the destination page. The May 5, 2026 removal of the account minimum opened the platform to every US advertiser, and OpenAI's self-service Ads Manager is now available to advertisers in more than 60 countries, so far more brands can hit this in their first days of testing. The 6-step playbook below prevents it.
      </P>

      <InlineCTA href="/tools/google-ads-to-chatgpt-ads-converter" label="Run the free Converter">
        Already have Google Ads campaigns? Run them through the free Converter to translate keywords into Context hints, ChatGPT-ready ad copy, and a convertibility score. Bake the OAI-AdsBot allowlist into your launch playbook before submitting your first ChatGPT campaign.
      </InlineCTA>

      {/* ── Section 7: 6-step HowTo + Visual 2 + Visual 3 ── */}
      <H2 id="howto-allowlist">How to allowlist OAI-AdsBot in 6 steps</H2>
      <P>
        This is the playbook. Each step is independent. Skip the steps that do not apply (no Cloudflare, no Akamai), but do not skip Step 5 (IP verification) and Step 6 (test with the Bot Access Checker).
      </P>

      <H3>Step 1. Add OAI-AdsBot to robots.txt</H3>
      <P>
        Add a User-agent block to your robots.txt with Allow: / so the crawler can fetch any landing page URL. This is the same block OpenAI uses in its own advertiser guidance, which also recommends allowing OAI-SearchBot.
      </P>
      <CodeBlock lang="robots.txt" syntax="text">{`# Allow OpenAI's ad-validation crawler (required for ChatGPT Ads)
User-agent: OAI-AdsBot
Allow: /

# OpenAI also recommends allowing its search crawler
User-agent: OAI-SearchBot
Allow: /`}</CodeBlock>

      <H3>Step 2. Allowlist the user-agent in Cloudflare WAF</H3>
      <P>
        Cloudflare&apos;s Bot Fight Mode and Super Bot Fight Mode may challenge or block OAI-AdsBot. If you use Super Bot Fight Mode, create a WAF custom rule that matches the user-agent and skips it. Bot Fight Mode is different: <A href="https://developers.cloudflare.com/bots/get-started/bot-fight-mode/">Cloudflare says</A> custom rules cannot skip it, so if it is challenging OAI-AdsBot, switch it off.
      </P>
      <CodeBlock lang="Cloudflare WAF Custom Rule (expression syntax)" syntax="text">{`# Cloudflare WAF Custom Rule

(http.user_agent contains "OAI-AdsBot/1.0")

Action: Skip
Skip:
  - All remaining custom rules
  - Super Bot Fight Mode

# Optional: log matched requests for verification
Log: Yes`}</CodeBlock>

      <H3>Step 3. Allowlist in Akamai, Fastly, or other CDN edge</H3>
      <P>
        If you run a CDN beyond or instead of Cloudflare, repeat the user-agent allowlist there. Akamai Bot Manager, Fastly's signal sciences module, Imperva, and DataDome all treat unknown bots as suspicious by default and may block OAI-AdsBot before it reaches your origin.
      </P>
      <CodeBlock lang="Akamai Bot Manager (concept)" syntax="text">{`Bot Category: Custom Allow List
Match: User-Agent header contains "OAI-AdsBot"
Action: ALLOW (bypass bot scoring)
Tag for analytics: openai-ads-validation

# Repeat the conceptual rule in your CDN of choice.
# The allowlist must live at the edge layer, not just the origin.`}</CodeBlock>

      <H3>Step 4. Render landing-page content server-side</H3>
      <P>
        OAI-AdsBot's JavaScript execution capability is undocumented. JavaScript-only single-page apps may fail validation when the bot cannot execute client-side rendering. Render all above-the-fold landing-page content server-side or use SSR (server-side rendering). This eliminates the JS-execution risk entirely and is also a baseline AEO best practice for organic <A href="/glossary/ai-citation">AI citation</A> capture.
      </P>

      <H3>Step 5. Verify visits against OpenAI's published IP ranges</H3>
      <P>
        OpenAI publishes OAI-AdsBot's addresses at <A href="https://openai.com/adsbot.json">openai.com/adsbot.json</A> as a list of IP ranges. When a request in your logs claims to be OAI-AdsBot, check that its source IP falls inside one of them. A match is OpenAI; anything else is a client borrowing the name. If your firewall supports IP lists, point the allowlist at the published file, not at addresses copied from your logs, since OpenAI warns its crawler infrastructure may change.
      </P>
      <P>
        From a terminal, the check takes two commands:
      </P>
      <CodeBlock lang="IP range check (bash)" syntax="bash">{`# 1. Download OpenAI's published OAI-AdsBot IP ranges
curl -s https://openai.com/adsbot.json -o adsbot.json

# 2. Replace <SUSPECTED_IP> with the source IP from your access logs
python3 -c "import ipaddress,json; ip=ipaddress.ip_address('<SUSPECTED_IP>'); print(any(ip in ipaddress.ip_network(p.get('ipv4Prefix') or p.get('ipv6Prefix')) for p in json.load(open('adsbot.json'))['prefixes']))"

# True  = inside OpenAI's published OAI-AdsBot ranges
# False = not OpenAI, whatever the User-Agent header says`}</CodeBlock>

      <H3>Step 6. Check robots.txt with the AI Bot Access Checker</H3>
      <P>
        Run your landing page URL through the free <A href="/tools/ai-bot-checker">AI Bot Access Checker</A> to confirm your robots.txt allows OAI-AdsBot and to see whether the site is served through Cloudflare. The checker reads robots.txt rather than fetching the page as OAI-AdsBot, so pair it with the IP check in Step 5. Re-test after every WAF, CDN, or DNS change, and especially before launching a new ChatGPT Ads campaign. Bot allowlists drift when teams update infrastructure, and the checker catches robots.txt drift before it costs you a campaign.
      </P>

      <H3>What can still go wrong</H3>
      <P>
        Even with all 6 steps complete, ad approval can still surface unexpected failures. Patterns to check:
      </P>

      <DiagnosticTable />

      {/* ── Section 8: What we don't know yet ── */}
      <H2 id="open-questions">What we don't know about OAI-AdsBot yet</H2>
      <P>
        Two questions this list started with in April are now answered: OpenAI publishes adsbot.json, and its advertiser guidance says its crawlers respect robots.txt. These are still open:
      </P>
      <ol style={{ paddingLeft: 20, margin: "0 0 18px", color: TEXT_DIM }}>
                        <Li><Strong>Revisit cadence.</Strong> Does OAI-AdsBot crawl a landing page once at submission and never again? Re-crawl on a schedule? Re-validate when the page content changes? Unknown.</Li>
        <Li><Strong>JavaScript execution.</Strong> Does OAI-AdsBot execute JavaScript fully, partially, or not at all? Server-side rendering removes the question, but it would be useful to know definitively.</Li>
        <Li><Strong>Failure communication.</Strong> OpenAI says a rejected ad shows Not serving with a reason on hover, and lists blocked user agents or IP addresses among common causes. Whether that reason names a firewall block specifically, or reads as a general policy rejection, is not documented.</Li>
      </ol>
      <P>
        We will update this post as OpenAI clarifies any of these. If you have first-hand campaign data on validation failures, we want to hear about it.
      </P>

      {/* ── Section 9: AI Visibility Lift close ── */}
      <H2 id="ai-visibility-lift">The AI Visibility Lift: same crawler discipline, organic side</H2>
      <P>
        Allowlisting OAI-AdsBot is paid-side discipline. Allowlisting OAI-SearchBot, Anthropic's <A href="/blog/how-to-get-cited-by-microsoft-copilot">Claude-SearchBot</A>, and Microsoft's Bingbot, and keeping Google's Googlebot unblocked, is organic-side discipline. The same crawler-taxonomy rigor applies to both surfaces. Most paid-only ChatGPT Ads guides skip the organic side entirely. Most organic <A href="/glossary/answer-engine-optimization">AEO</A> guides skip OAI-AdsBot entirely. Both miss the symmetry.
      </P>
      <P>
        The simple version: every AI engine separates training crawlers from retrieval crawlers from user-initiated crawlers. The conservative-and-correct stance is the same across vendors. Block training crawlers (GPTBot, ClaudeBot, Google-Extended) unless you opt into training data inclusion. Allow retrieval crawlers (OAI-SearchBot, Claude-SearchBot, Bingbot, and Googlebot for Google's AI surfaces) unless you want to exit AI search citations. Allow user-initiated crawlers (ChatGPT-User, Claude-User, Perplexity-User) because the user explicitly asked. Allow ad-validation crawlers (OAI-AdsBot) because the alternative is broken campaigns.
      </P>
      <P>
        One discipline, applied across both paid and organic. Mid-market B2B teams that get this right early will compound a year of advantage by the time the playbook becomes general knowledge.
      </P>

      <InlineCTA href="/tools/ai-bot-checker" label="Check your bot access in 60 seconds">
        The free AI Bot Access Checker tests your robots.txt against 25 AI crawlers, including OpenAI's full 4-bot stack, and flags whether your site is served through Cloudflare. No signup. Re-test after every infrastructure change.
      </InlineCTA>

      {/* ── HowTo JSON-LD ── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to allowlist OAI-AdsBot for ChatGPT Ads landing page validation",
        "description": "A 6-step playbook for allowlisting OpenAI's OAI-AdsBot crawler so it can validate ChatGPT ad landing pages without being blocked by Cloudflare, Akamai, or other WAF or CDN bot protection.",
        "totalTime": "PT45M",
        "step": howtoSteps.map((s, i) => ({
          "@type": "HowToStep",
          "position": i + 1,
          "name": s.name,
          "text": s.text,
        })),
      }) }} />

      {/* ── FAQPage JSON-LD ── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/chatgpt-ads-opens-to-all-us-advertisers">ChatGPT Ads Just Opened to Every US Advertiser: What Marketers Should Do First</A></Li>
        <Li><A href="/tools/ai-bot-checker">AI Bot Access Checker (free tool)</A></Li>
        <Li><A href="/tools/google-ads-to-chatgpt-ads-converter">Google Ads to ChatGPT Ads Converter (free tool)</A></Li>
        <Li><A href="/blog/how-chatgpt-ads-work">How Do ChatGPT Ads Work? Format, Context Hints, and the AI Visibility Lift (October 2026 Update)</A></Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)</A></Li>
        <Li><A href="/blog/chatgpt-ads-complete-guide">What Are ChatGPT Ads? The Complete Guide for 2026</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "what-is-oai-adsbot",
  title: "What Is OAI-AdsBot? (OpenAI's ChatGPT Ads Crawler)",
  excerpt: "OpenAI added OAI-AdsBot to its public crawler documentation on April 23, 2026. It checks ChatGPT ad landing pages, its data is excluded from training, and OpenAI now publishes its IP addresses. Here is how OAI-AdsBot fits into the 4-bot OpenAI taxonomy and a 6-step allowlist playbook with IP verification.",
  metaDescription: "OAI-AdsBot validates ChatGPT ad landing pages. Documented April 23, 2026. Full 4-bot taxonomy + 6-step allowlist playbook with IP verification.",
  date: "2026-04-30",
  lastUpdated: "2026-10-17",
  readTime: "12 min read",
  category: "AI Ads",
  tags: ["OAI-AdsBot", "ChatGPT Ads", "AI Crawlers", "robots.txt", "Cloudflare", "WAF"],
  Content,
};

export default post;
